/**
 * Offline User Data Hooks
 * 
 * Hooks for accessing user profile and preference data with offline support.
 * Provides cache-first loading with intelligent sync strategies for user data.
 */

import { useState, useEffect, useCallback } from 'react';
import { useOfflineState } from '../useOfflineState';
import { indexedDBService } from '@/services/offline/indexeddb';
import { usePermissions } from '@/contexts/unified-permissions';
import type { UserProfileOffline } from '@/services/offline/indexeddb/schema';
import type { DataFetchOptions, DataFetchResult } from './shared';

/**
 * Hook for user profile with offline support
 * 
 * Fetches the current user's profile with cache-first loading.
 * Automatically handles sync when online and refresh is requested.
 * 
 * @param options - Fetch options including refresh behavior
 * @returns User profile data with loading states and refresh capability
 */
export function useOfflineUserProfile(
  options: DataFetchOptions = {}
): DataFetchResult<UserProfileOffline> {
  const { state } = useOfflineState();
  const { user } = usePermissions();
  const [data, setData] = useState<UserProfileOffline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const { forceRefresh = false } = options;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const cachedData = await indexedDBService.userProfile.getCurrentProfile(user?.id);
      setData(cachedData);
      setIsFromCache(true);
      setLastUpdated(new Date().toISOString());

      if (state.isOnline && forceRefresh) {
        try {
          // Import sync service dynamically to avoid circular dependencies
          const { syncService } = await import('@/services/offline');

          // Only sync if not already syncing to prevent loops
          if (!syncService.getStatus().isSyncing) {
            await syncService.performSync();
          }
          const updatedData = await indexedDBService.userProfile.getCurrentProfile(user?.id);
          setData(updatedData);
          setIsFromCache(false);
        } catch (syncError) {
          console.error('Sync failed:', syncError);
          // Continue with cached data
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user profile';
      setError(errorMessage);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [state.isOnline, forceRefresh, user?.id]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh
  };
}

/**
 * Enhanced hook for profile page with save functionality and offline support
 * 
 * Provides additional functionality for profile management including save operations.
 * Handles profile updates with proper offline queue management.
 * 
 * @returns Enhanced profile data with save functionality and state management
 */
export function useOfflineProfile(): {
  profile: UserProfileOffline | null;
  loading: boolean;
  error: string | null;
  isFromCache: boolean;
  lastUpdated: string | null;
  refresh: () => Promise<void>;
  saveProfile: (profileData: Record<string, unknown>, avatarUrl?: string | null) => Promise<void>;
  setProfile: (profile: UserProfileOffline | null) => void;
} {
  const { state } = useOfflineState();
  const { user } = usePermissions();
  const [profile, setProfile] = useState<UserProfileOffline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const cachedData = await indexedDBService.userProfile.getCurrentProfile(user?.id);
      setProfile(cachedData);
      setIsFromCache(true);
      setLastUpdated(new Date().toISOString());

      // For profile data, we only use cached data unless explicitly refreshed
      // Sync should only happen during initial app load or manual refresh
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch profile';
      setError(errorMessage);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const saveProfile = useCallback(async (profileData: Record<string, unknown>, avatarUrl?: string | null) => {
    if (!profile) {
      throw new Error('No profile loaded');
    }

    try {
      // Update local profile immediately for optimistic UI
      const updatedProfile: UserProfileOffline = {
        ...profile,
        ...profileData,
        avatar_url: avatarUrl !== undefined ? (avatarUrl || undefined) : profile.avatar_url,
        updated_at: new Date().toISOString()
      };

      setProfile(updatedProfile);

      // Save to IndexedDB
      await indexedDBService.userProfile.update(profile.id, updatedProfile);

      // If online, sync immediately
      if (state.isOnline) {
        try {
          const { syncService } = await import('@/services/offline');
          if (!syncService.getStatus().isSyncing) {
            await syncService.performSync();
          }
        } catch (syncError) {
          console.error('Sync failed after profile save:', syncError);
          // Profile is still saved locally, sync will happen later
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save profile';
      setError(errorMessage);
      throw err;
    }
  }, [profile, state.isOnline]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    profile,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh,
    saveProfile,
    setProfile
  };
}
